import { SearchAccordion as ComponentSearchAccordion } from '@/components';
import { OrganizationTemplateSearchFormSchema } from '@/features/organization-template/_models/organization-template';
import { useOrganizationTemplatePageSearchQueryStates } from '@/features/organization-template/_utils/search-query-states';
import {
  useCreateQueryStore,
  useQueryStore,
} from '@/features/search/_hooks/query-store';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import isEqual from 'lodash-es/isEqual';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import type { z } from 'zod';
import { OrganizationTemplateSearchForm } from './OrganizationTemplateSearchForm';

type FormValues = z.infer<typeof OrganizationTemplateSearchFormSchema>;

export const OrganizationTemplateSearchAccordion: FC = () => {
  const { searchQueryStates, setSearchQueryStates } =
    useOrganizationTemplatePageSearchQueryStates();

  const { mutateAsync } = useCreateQueryStore();
  const treeParamsData = useQueryStore(searchQueryStates.cascader ?? undefined);

  const defaultValues = useMemo<FormValues>(() => {
    return {
      name: searchQueryStates.q ?? '',
      nestedOrganizationIds: treeParamsData.data?.query?.organizationIds ?? [],
    };
  }, [searchQueryStates.q, treeParamsData.data?.query?.organizationIds]);

  const formMethods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(OrganizationTemplateSearchFormSchema),
  });

  const { setValue } = formMethods;

  useEffect(() => {
    if (defaultValues.nestedOrganizationIds?.length) {
      setValue('nestedOrganizationIds', defaultValues.nestedOrganizationIds);
    }
  }, [setValue, defaultValues.nestedOrganizationIds]);

  const onSearch = useCallback(
    async ({ q, nestedOrganizationIds }: FormValues) => {
      const queryAndLeafs = generateQueryAndLeafs({
        organizationIds: nestedOrganizationIds,
      });

      const currLeafs = queryAndLeafs?.queryLeafs;
      const prevLeafs = treeParamsData.data?.queryLeafs;

      const isDifferent = currLeafs && !isEqual(currLeafs, prevLeafs);
      const requestCascaderId = nestedOrganizationIds.length
        ? isDifferent
          ? (await mutateAsync(queryAndLeafs)).id
          : searchQueryStates.cascader
        : null;

      const nextSearchQueryStates = {
        cascader: requestCascaderId,
        q: q || null,
        page: 1,
      };
      setSearchQueryStates(nextSearchQueryStates);
    },
    [
      mutateAsync,
      setSearchQueryStates,
      searchQueryStates.cascader,
      treeParamsData.data?.queryLeafs,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <ComponentSearchAccordion
        defaultOpen={true}
        filterTags={null}
        searchForm={
          <OrganizationTemplateSearchForm
            onSearch={onSearch}
            resetFormValues={{
              q: '',
              nestedOrganizationIds: [],
            }}
          />
        }
      />
    </FormProvider>
  );
};
